import React from 'react';
import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';

import { CS_FONT, CS_NARROW, CS_WEIGHT, csTokens } from 'components/CallScribe/callScribeTheme';

export type TFieldHelperTone = 'info' | 'ok' | 'bad';

export const fieldHelpId = (inputId: string): string => `${inputId}-help`;

const useFieldStyles = makeStyles((theme: Theme) => {
  const t = csTokens(theme);
  return {
    root: {
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0,
    },
    label: {
      ...CS_FONT.caption,
      fontWeight: CS_WEIGHT.strong,
      letterSpacing: '0.02em',
      color: t.text2,
      margin: '0 0 6px 2px',
    },
    labelHidden: {
      position: 'absolute',
      width: 1,
      height: 1,
      margin: -1,
      padding: 0,
      overflow: 'hidden',
      clip: 'rect(0 0 0 0)',
      whiteSpace: 'nowrap',
      border: 0,
    },
    shell: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      minHeight: 52,
      boxSizing: 'border-box',
      padding: '0 6px',
      background: t.surface,
      border: `1px solid ${t.fieldLine}`,
      borderRadius: 12,
      transition: 'border-color 150ms ease-out, box-shadow 150ms ease-out',
      '&:focus-within': {
        borderColor: t.focus,
        boxShadow: `0 0 0 1px ${t.focus}`,
      },
      [CS_NARROW]: { gap: 4, padding: '0 4px' },
    },
    shellInvalid: {
      borderColor: t.neg,
    },
    input: {
      flex: '1 1 auto',
      minWidth: 0,
      height: 48,
      padding: '0 4px',
      border: 'none',
      outline: 'none',
      background: 'transparent',
      color: t.text,
      fontFamily: 'inherit',
      fontSize: 18,
      lineHeight: '24px',
      fontWeight: CS_WEIGHT.strong,
      fontVariantNumeric: 'tabular-nums',
      '&::placeholder': {
        color: t.text3,
        opacity: 1,
        fontSize: 16,
        fontWeight: CS_WEIGHT.regular,
      },
      [CS_NARROW]: { fontSize: 16 },
    },
    helperRow: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: 8,
      minWidth: 0,
    },
    helper: {
      flex: '1 1 auto',
      minWidth: 0,
      display: 'flex',
      alignItems: 'flex-start',
      gap: 6,
      minHeight: 20,
      padding: '6px 4px 0',
      '& svg': { fontSize: 14, marginTop: 1, flex: '0 0 auto' },
    },
    helperText: {
      ...CS_FONT.caption,
      minWidth: 0,
    },
    info: { color: t.text2 },
    ok: { color: t.pos },
    bad: { color: t.neg },
  };
});

const HELPER_ICON: Record<TFieldHelperTone, React.ReactElement> = {
  info: <InfoOutlinedIcon aria-hidden />,
  ok: <CheckRoundedIcon aria-hidden />,
  bad: <WarningAmberRoundedIcon aria-hidden />,
};

export interface IFieldProps {
  label: string;
  inputId: string;
  helper?: string;
  helperTone?: TFieldHelperTone;
  invalid?: boolean;
  helperAction?: React.ReactNode;
  hideLabel?: boolean;
  children: React.ReactNode;
}

const Field: React.FC<IFieldProps> = ({
  label,
  inputId,
  helper,
  helperTone = 'info',
  invalid = false,
  helperAction,
  hideLabel = false,
  children,
}) => {
  const classes = useFieldStyles();
  return (
    <div className={classes.root}>
      <label
        htmlFor={inputId}
        className={hideLabel ? `${classes.label} ${classes.labelHidden}` : classes.label}
      >
        {label}
      </label>
      <div data-field-shell className={`${classes.shell} ${invalid ? classes.shellInvalid : ''}`}>
        {children}
      </div>
      {helper !== undefined || helperAction ? (
        <div className={classes.helperRow}>
          <div
            id={fieldHelpId(inputId)}
            data-tone={helperTone}
            aria-live="polite"
            className={`${classes.helper} ${classes[helperTone]}`}
          >
            {helper ? HELPER_ICON[helperTone] : null}
            <span className={`${classes.helperText} ${classes[helperTone]}`}>{helper}</span>
          </div>
          {helperAction}
        </div>
      ) : null}
    </div>
  );
};

export type TFieldInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'className'>;

export const FieldInput = React.forwardRef<HTMLInputElement, TFieldInputProps>((props, ref) => {
  const classes = useFieldStyles();
  return <input ref={ref} {...props} className={classes.input} />;
});

FieldInput.displayName = 'FieldInput';

export default Field;
